import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Modal,
  IconButton,
  TextField,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BuildIcon from "@mui/icons-material/Build";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";
import { AssetDetailsAPI } from "../viewmodel/AssetDetailsAPI";

const RESULTS = ["Fixed", "Not fixed"];

// seconds -> "dd/mm/yyyy, hh:mm am/pm"
const formatDateTime = (value) => {
  if (!value || isNaN(Number(value))) return "-";
  return new Date(Number(value) * 1000).toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const formatCost = (cost) =>
  cost === null || cost === undefined
    ? "-"
    : `₹${Number(cost).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const paper = {
  backgroundColor: "#ffffff",
  boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.064)",
  padding: "20px",
  borderRadius: "5px",
  position: "relative",
  width: "420px",
  maxWidth: "90vw",
};

// Repair / maintenance section on the asset details page
const RepairSection = ({ assetId, canEdit, isRetired, onChanged }) => {
  const { handleToastOpen } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [backOpen, setBackOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ issue: "", vendor: "", notes: "" });
  const [back, setBack] = useState({ result: "", cost: "", return_notes: "" });

  const load = async () => {
    try {
      setLoading(true);
      setRows(await AssetDetailsAPI.fetchRepairHistory(assetId));
    } catch (error) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (assetId) load();
  }, [assetId]);

  const openRepair = rows.find((r) => r.is_open);

  const submitSend = async () => {
    try {
      setSaving(true);
      await AssetDetailsAPI.sendForRepair(assetId, form);
      handleToastOpen("success", "Asset sent for repair");
      setSendOpen(false);
      setForm({ issue: "", vendor: "", notes: "" });
      await load();
      if (onChanged) onChanged();
    } catch (error) {
      handleToastOpen("error", error.response?.data?.message || "Failed to send for repair");
    } finally {
      setSaving(false);
    }
  };

  const submitBack = async () => {
    try {
      setSaving(true);
      await AssetDetailsAPI.markRepairBack(assetId, back);
      handleToastOpen("success", "Asset is back from repair");
      setBackOpen(false);
      setBack({ result: "", cost: "", return_notes: "" });
      await load();
      if (onChanged) onChanged();
    } catch (error) {
      handleToastOpen("error", error.response?.data?.message || "Failed to mark as back");
    } finally {
      setSaving(false);
    }
  };

  const costInvalid = back.cost !== "" && (isNaN(Number(back.cost)) || Number(back.cost) < 0);

  return (
    <>
      {/* Orange box while the asset is at repair */}
      {openRepair && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
            backgroundColor: "#fff3e0",
            border: "1px solid #ffcc80",
            borderRadius: "8px",
            p: "12px 16px",
            mt: "1em",
          }}
        >
          <BuildIcon sx={{ color: "#e65100" }} />
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography sx={{ fontWeight: 600, color: "#e65100" }}>
              At repair since {formatDateTime(openRepair.sent_at)}
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "#555" }}>
              {openRepair.vendor ? `${openRepair.vendor} – ` : ""}
              {openRepair.issue}
            </Typography>
          </Box>
          {canEdit && (
            <Button variant="contained" color="success" size="small" onClick={() => setBackOpen(true)}>
              Mark as back
            </Button>
          )}
        </Box>
      )}

      {/* Repair history */}
      <Box
        sx={{
          border: "1px solid #3CB043",
          borderRadius: "1.2em 1.2em 1em 1em",
          overflow: "hidden",
          mt: "1em",
        }}
      >
        <Box
          sx={{
            backgroundColor: "#3CB043",
            color: "#fff",
            p: ".5em 1em",
            minHeight: "48px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <span style={{ fontSize: "1.1em" }}>Repair History</span>
          {canEdit && !openRepair && !isRetired && (
            <Button
              size="small"
              variant="contained"
              startIcon={<BuildIcon />}
              onClick={() => setSendOpen(true)}
              sx={{
                position: "absolute",
                right: "0.8em",
                backgroundColor: "#fff",
                color: "#2b6b2f",
                "&:hover": { backgroundColor: "#f2f8f2" },
              }}
            >
              Send for repair
            </Button>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography sx={{ p: 2, textAlign: "center", color: "#666", fontSize: "0.9em" }}>
            No repairs yet
          </Typography>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  {["Sent", "Issue", "Vendor", "Back", "Result", "Cost", "Notes"].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#f2f8f2",
                        color: "#2b6b2f",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatDateTime(r.sent_at)}
                      {r.sent_by && (
                        <div style={{ fontSize: "0.75em", color: "#888" }}>by {r.sent_by}</div>
                      )}
                    </TableCell>
                    <TableCell>{r.issue}</TableCell>
                    <TableCell>{r.vendor || "-"}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {r.is_open ? (
                        <span
                          style={{
                            backgroundColor: "#fff3e0",
                            color: "#e65100",
                            fontSize: "0.75em",
                            padding: "2px 10px",
                            borderRadius: "10px",
                            fontWeight: 600,
                          }}
                        >
                          At repair
                        </span>
                      ) : (
                        <>
                          {formatDateTime(r.returned_at)}
                          {r.returned_by && (
                            <div style={{ fontSize: "0.75em", color: "#888" }}>
                              by {r.returned_by}
                            </div>
                          )}
                        </>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.result ? (
                        <span
                          style={{
                            backgroundColor: r.result === "Fixed" ? "#e8f5e9" : "#ffebee",
                            color: r.result === "Fixed" ? "#2e7d32" : "#c62828",
                            fontSize: "0.75em",
                            padding: "2px 10px",
                            borderRadius: "10px",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {r.result}
                        </span>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{formatCost(r.cost)}</TableCell>
                    <TableCell sx={{ fontSize: "0.8em", color: "#555" }}>
                      {[r.notes, r.return_notes].filter(Boolean).join(" · ") || "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Popup: send for repair */}
      <Modal
        open={sendOpen}
        onClose={() => !saving && setSendOpen(false)}
        style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Box sx={paper}>
          <IconButton
            onClick={() => setSendOpen(false)}
            sx={{ position: "absolute", top: 8, right: 8, color: "#555" }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Send for Repair
          </Typography>
          <TextField
            label="Issue"
            placeholder="e.g. Battery not charging"
            value={form.issue}
            onChange={(e) => setForm({ ...form, issue: e.target.value })}
            fullWidth
            required
            size="small"
            disabled={saving}
            inputProps={{ maxLength: 255 }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Vendor (optional)"
            placeholder="e.g. Lenovo Service Centre"
            value={form.vendor}
            onChange={(e) => setForm({ ...form, vendor: e.target.value })}
            fullWidth
            size="small"
            disabled={saving}
            inputProps={{ maxLength: 150 }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Notes (optional)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            fullWidth
            multiline
            rows={2}
            disabled={saving}
            inputProps={{ maxLength: 500 }}
          />
          <Typography variant="caption" sx={{ display: "block", color: "#666", mt: 1 }}>
            Status becomes Maintenance. If an employee has this asset, it stays assigned to them.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
            <Button variant="contained" color="secondary" onClick={() => setSendOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={submitSend}
              disabled={!form.issue.trim() || saving}
              sx={{ backgroundColor: "#052660" }}
            >
              {saving ? <CircularProgress size={20} color="inherit" /> : "Send"}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Popup: back from repair */}
      <Modal
        open={backOpen}
        onClose={() => !saving && setBackOpen(false)}
        style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Box sx={paper}>
          <IconButton
            onClick={() => setBackOpen(false)}
            sx={{ position: "absolute", top: 8, right: 8, color: "#555" }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Back from Repair
          </Typography>
          {openRepair && (
            <Typography variant="body2" sx={{ mb: 2, color: "#555" }}>
              Issue: {openRepair.issue}
            </Typography>
          )}
          <TextField
            select
            label="Result"
            value={back.result}
            onChange={(e) => setBack({ ...back, result: e.target.value })}
            fullWidth
            required
            size="small"
            disabled={saving}
            sx={{ mb: 2 }}
          >
            {RESULTS.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Cost in ₹ (optional)"
            placeholder="e.g. 0 if under warranty"
            value={back.cost}
            onChange={(e) => setBack({ ...back, cost: e.target.value })}
            fullWidth
            size="small"
            disabled={saving}
            inputProps={{ inputMode: "decimal" }}
            error={costInvalid}
            helperText={costInvalid ? "Enter a number (0 or more)" : " "}
          />
          <TextField
            label="Notes (optional)"
            placeholder="e.g. Battery replaced"
            value={back.return_notes}
            onChange={(e) => setBack({ ...back, return_notes: e.target.value })}
            fullWidth
            multiline
            rows={2}
            disabled={saving}
            inputProps={{ maxLength: 500 }}
          />
          {back.result === "Not fixed" && (
            <Typography variant="caption" sx={{ display: "block", color: "#c62828", mt: 1 }}>
              If it can't be used any more, you can Retire it after this.
            </Typography>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
            <Button variant="contained" color="secondary" onClick={() => setBackOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={submitBack}
              disabled={!back.result || costInvalid || saving}
            >
              {saving ? <CircularProgress size={20} color="inherit" /> : "Mark as back"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default RepairSection;